"use client";

import * as SelectPrimitive from "@radix-ui/react-select";
import { useMeasure } from "@uidotdev/usehooks";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { motion } from "motion/react";
import * as React from "react";
import { cn } from "../utils/classnames";

// A prettier select with some animations
const labelVariants = {
  touched: {
    fontSize: "12px",
    opacity: "100%",
    top: "-8px",
  },
  untouched: {
    backgroundColor: "rgba(0,0,0,0)",
    fontSize: "14px",
    opacity: "0%",
    top: "16px",
  },
};

const backgroundVariants = {
  touched: ({ width, height }: { width: number; height: number }) => ({
    backgroundColor: "#fff",
    height: `${height}px`,
    top: "-8px",
    width: `${width}px`,
  }),
  untouched: {
    height: "14px",
    top: "-8px",
    width: "0%",
  },
};

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  label?: string;
  ariaLabel?: string;
  chevronIcon?: React.ReactNode;
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(
  (
    {
      className,
      children,
      label,
      ariaLabel,
      chevronIcon = (
        <ChevronDown className="h-4 w-4 opacity-50 transition-transform duration-200" />
      ),
      ...props
    },
    ref,
  ) => {
    const [labelRef, bounds] = useMeasure();
    const width = bounds?.width || 0;
    const height = bounds?.height || 0;

    return (
      <motion.div
        className="group relative"
        transition={{ staggerChildren: 1 }}
      >
        <SelectPrimitive.Trigger
          aria-label={ariaLabel || label}
          className={cn(
            "h-12 w-full rounded-sm border border-new-beginnings-base bg-white p-2 text-new-beginnings-base focus:outline focus:outline-1 focus:outline-new-beginnings-base",
            "flex items-center justify-between px-3 py-2 text-base disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground [&>span]:line-clamp-1",
            "[&[data-state=open]_svg]:rotate-180",
            className,
          )}
          ref={ref}
          {...props}
        >
          {children}
          {chevronIcon && (
            <SelectPrimitive.Icon asChild>{chevronIcon}</SelectPrimitive.Icon>
          )}
        </SelectPrimitive.Trigger>
        <motion.div
          animate={"touched"}
          className="absolute left-[9px] z-[40] min-w-fit text-xs"
          custom={{ height, width }}
          initial="untouched"
          variants={backgroundVariants}
        />
        {label ? (
          <motion.span
            animate={"touched"}
            className="absolute left-[9px] z-[40] px-1 text-new-beginnings-base text-xs"
            exit="untouched"
            initial="untouched"
            ref={labelRef}
            variants={labelVariants}
          >
            {label}
          </motion.span>
        ) : null}
      </motion.div>
    );
  },
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    ref={ref}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    ref={ref}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => {
  return (
    <SelectPrimitive.Portal>
      <motion.div
        className="group relative"
        transition={{ staggerChildren: 1 }}
      >
        <SelectPrimitive.Content
          className={cn(
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-[2000] max-h-[--radix-select-content-available-height] min-w-[8rem] origin-[--radix-select-content-transform-origin] overflow-y-auto overflow-x-hidden rounded-sm border bg-popover text-popover-foreground shadow-md data-[state=closed]:animate-out data-[state=open]:animate-in",
            position === "popper" &&
              "data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1",
            className,
          )}
          position={position}
          ref={ref}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              "p-1",
              position === "popper" &&
                "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </motion.div>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    className={cn("py-1.5 pr-2 pl-8 font-semibold text-base", className)}
    ref={ref}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pr-2 pl-8 text-base outline-none hover:cursor-pointer hover:bg-new-beginnings-base hover:text-support-base data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    ref={ref}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    ref={ref}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
